import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Image,
  Eye,
  Save,
  Send,
  ArrowRight,
  Upload,
  X,
  Info,
  Lock,
  Crown,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubmitPR = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  
  const isPro = user?.is_pro || (user?.total_deposited >= 1000);
  const userBalance = user?.balance || 0;

  const [formData, setFormData] = useState({
    title: "",
    summary: "",
    body: "",
    topic: "",
    city: "",
    country: "",
    company_boilerplate: "",
    video_url: "",
    publish_timing: "asap",
    scheduled_date: "",
    white_label: false,
    public_org: "",
    public_website: "",
    public_name: "",
    public_email: "",
    public_phone: "",
    public_address: "",
    internal_org: "",
    internal_website: "",
    internal_name: "",
    internal_email: "",
    internal_phone: "",
    internal_address: "",
  });

  const topics = [
    "Business",
    "Technology",
    "Finance",
    "Healthcare",
    "Entertainment",
    "Sports",
    "Education",
    "Real Estate",
    "Automotive",
    "Travel",
    "Food & Beverage",
    "Fashion",
    "Energy",
    "Environment",
    "Legal",
    "Other",
  ];

  useEffect(() => {
    fetchChannels();
  }, []);

  // Calculate total cost when channels change
  useEffect(() => {
    if (channels.length > 0 && selectedChannels.length > 0) {
      const cost = selectedChannels.reduce((total, channelId) => {
        const channel = channels.find(c => c.channel_id === channelId);
        if (channel) {
          return total + (isPro ? channel.discounted_price : channel.regular_price);
        }
        return total;
      }, 0);
      setTotalCost(cost);
    } else {
      setTotalCost(0);
    }
  }, [selectedChannels, channels, isPro]);

  const fetchChannels = async () => {
    try {
      const response = await fetch(`${API_URL}/api/distribution-channels`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setChannels(data);
      }
    } catch (error) {
      console.error("Failed to fetch channels:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChannelToggle = (channelId) => {
    setSelectedChannels((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId]
    );
  };

  const handleSelectAllChannels = () => {
    if (selectedChannels.length === channels.length) {
      setSelectedChannels([]);
    } else {
      setSelectedChannels(channels.map((c) => c.channel_id));
    }
  };

  const getChannelPrice = (channel) => {
    return isPro ? channel.discounted_price : channel.regular_price;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        credentials: "include",
        body: formDataUpload,
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments((prev) => [...prev, { id: data.file_id, name: file.name }]);
        toast.success("File uploaded successfully");
      } else {
        toast.error("Failed to upload file");
      }
    } catch (error) {
      toast.error("Upload error");
    }
  };

  const removeDocument = (docId) => {
    setDocuments((prev) => prev.filter((d) => d.id !== docId));
  };

  const countWords = (text) => {
    return text.trim().split(/\s+/).filter((w) => w).length;
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/press-releases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          distribution_channels: selectedChannels,
          status: "draft",
        }),
      });

      if (response.ok) {
        toast.success("Draft saved successfully");
        navigate("/manage-pr");
      } else {
        toast.error("Failed to save draft");
      }
    } catch (error) {
      toast.error("Error saving draft");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error("Please enter a headline");
      return;
    }
    if (selectedChannels.length === 0) {
      toast.error("Please select at least one distribution channel");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/press-releases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          distribution_channels: selectedChannels,
          status: "pending",
        }),
      });

      if (response.ok) {
        toast.success("Press release submitted for review");
        navigate("/manage-pr");
      } else {
        try {
          const errorData = await response.json();
          toast.error(errorData.detail || "Failed to submit press release");
        } catch {
          toast.error("Failed to submit press release");
        }
      }
    } catch (error) {
      toast.error("Error submitting press release");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-slate-900">
          Submit Press Release
        </h1>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 1 ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"
              }`}
            >
              1
            </div>
            <span className={step >= 1 ? "text-slate-900 font-medium" : "text-slate-400"}>
              Draft Press Release
            </span>
          </div>
          <div className="w-8 h-px bg-slate-300" />
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 2 ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"
              }`}
            >
              2
            </div>
            <span className={step >= 2 ? "text-slate-900 font-medium" : "text-slate-400"}>
              Preview & Submit
            </span>
          </div>
        </div>
      </div>

      {step === 1 && (
        <>
          {/* Distribution Details */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Distribution Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Channel Selection */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base font-medium">
                    Select Distribution Channels <span className="text-red-500">*</span>
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllChannels}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="select-all-channels-btn"
                  >
                    {selectedChannels.length === channels.length ? "Deselect All" : "Select All"}
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {channels.map((channel) => (
                    <div
                      key={channel.channel_id}
                      className={`flex flex-col p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedChannels.includes(channel.channel_id)
                          ? "border-blue-300 bg-blue-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      onClick={() => handleChannelToggle(channel.channel_id)}
                      data-testid={`channel-${channel.channel_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedChannels.includes(channel.channel_id)}
                          onCheckedChange={() => handleChannelToggle(channel.channel_id)}
                        />
                        <span className="text-lg">{channel.icon}</span>
                        <span className="text-sm font-medium text-slate-700 flex-1">
                          {channel.name}
                        </span>
                        {channel.is_new && (
                          <Badge className="bg-emerald-500 text-white">NEW</Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-2 pl-9">
                        <span className="text-lg font-bold text-blue-600">
                          ${isPro ? channel.discounted_price : channel.regular_price}
                        </span>
                        {!isPro && (
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-slate-400 line-through">${channel.discounted_price}</span>
                            <Badge variant="outline" className="text-xs border-amber-300 text-amber-600">
                              <Crown className="h-3 w-3 mr-1" />
                              PRO
                            </Badge>
                          </div>
                        )}
                        {isPro && (
                          <span className="text-sm text-slate-400 line-through">${channel.regular_price}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Cost Summary */}
                {selectedChannels.length > 0 && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Selected: {selectedChannels.length} channel(s)</p>
                        <p className="text-2xl font-bold text-slate-900">Total: ${totalCost.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">Your Balance</p>
                        <p className={`text-xl font-bold ${userBalance >= totalCost ? 'text-emerald-600' : 'text-red-600'}`}>
                          ${userBalance.toFixed(2)}
                        </p>
                        {userBalance < totalCost && (
                          <Button 
                            variant="link" 
                            className="text-blue-600 p-0 h-auto text-sm"
                            onClick={() => navigate('/settings')}
                          >
                            Add Funds →
                          </Button>
                        )}
                      </div>
                    </div>
                    {!isPro && (
                      <p className="text-xs text-amber-600 mt-2">
                        💡 Deposit $1000+ to unlock PRO rates and save on every distribution!
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Publication Timing */}
              <div>
                <Label className="text-base font-medium mb-4 block">
                  Publication Timing <span className="text-red-500">*</span>
                </Label>
                <RadioGroup
                  value={formData.publish_timing}
                  onValueChange={(v) => handleChange("publish_timing", v)}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="asap" id="asap" />
                    <Label htmlFor="asap" className="font-normal cursor-pointer">
                      Publish as soon as possible (ASAP)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="scheduled" id="scheduled" />
                    <Label htmlFor="scheduled" className="font-normal cursor-pointer">
                      Schedule for later
                    </Label>
                  </div>
                </RadioGroup>
                {formData.publish_timing === "scheduled" && (
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_date}
                    onChange={(e) => handleChange("scheduled_date", e.target.value)}
                    className="mt-3 w-64"
                    data-testid="scheduled-date-input"
                  />
                )}
              </div>

              {/* White Label */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={formData.white_label}
                      onCheckedChange={(v) => handleChange("white_label", v)}
                      data-testid="white-label-switch"
                    />
                    <div>
                      <Label className="font-medium">White-label distribution</Label>
                      <p className="text-sm text-slate-500">
                        Receive customizable distribution report without PR Link Boost branding
                      </p>
                    </div>
                  </div>
                  <Info className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Press Release Details */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Press Release Details</CardTitle>
              <Button variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Topic */}
              <div>
                <Label>
                  Topic <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.topic}
                  onValueChange={(v) => handleChange("topic", v)}
                >
                  <SelectTrigger className="mt-2" data-testid="topic-select">
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {topics.map((topic) => (
                      <SelectItem key={topic} value={topic}>
                        {topic}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Headline */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>
                    Headline <span className="text-red-500">*</span>
                  </Label>
                  <span className="text-sm text-slate-500">
                    {formData.title.length}/250
                  </span>
                </div>
                <Input
                  value={formData.title}
                  onChange={(e) =>
                    handleChange("title", e.target.value.slice(0, 250))
                  }
                  placeholder="Enter your headline here (250 characters)"
                  className="mt-2"
                  data-testid="headline-input"
                />
              </div>

              {/* Summary */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>Summary</Label>
                  <span className="text-sm text-slate-500">
                    {countWords(formData.summary)}/45 words
                  </span>
                </div>
                <Textarea
                  value={formData.summary}
                  onChange={(e) => handleChange("summary", e.target.value)}
                  placeholder="Enter a 1-2 sentence summary highlighting the main announcement (max. 45 words). Longer summaries may be trimmed."
                  className="mt-2 min-h-[80px]"
                  data-testid="summary-textarea"
                />
              </div>

              {/* City and Country */}
              <div>
                <Label>
                  City and Country <span className="text-red-500">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <Input
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="City"
                    data-testid="city-input"
                  />
                  <Input
                    value={formData.country}
                    onChange={(e) => handleChange("country", e.target.value)}
                    placeholder="Country"
                    data-testid="country-input"
                  />
                </div>
              </div>

              {/* Body */}
              <div>
                <Label>
                  Body <span className="text-red-500">*</span>
                </Label>
                <p className="text-sm text-amber-600 mt-1 mb-2">
                  To maximize distribution and acceptance by all media outlets, aim for a body under 500 words.{" "}
                  <span className="font-semibold">Content over 500 words may be truncated</span> by some channels.
                </p>
                {/* Editor Toolbar */}
                <div className="border border-slate-200 rounded-t-lg bg-slate-50 p-2 flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Image className="h-4 w-4" />
                  </Button>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                    Add Image
                  </span>
                  <div className="w-px h-6 bg-slate-300 mx-2" />
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Bold className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Italic className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Underline className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-slate-300 mx-2" />
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <List className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <ListOrdered className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-slate-300 mx-2" />
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Link className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea
                  value={formData.body}
                  onChange={(e) => handleChange("body", e.target.value)}
                  placeholder="Insert text here..."
                  className="min-h-[300px] rounded-t-none border-t-0"
                  data-testid="body-textarea"
                />
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                  <span>Word Count: {countWords(formData.body)} / 1200</span>
                  <span>Anchor links: 0 / 10</span>
                </div>
              </div>

              {/* Company Boilerplate */}
              <div>
                <Label>Company Boilerplate</Label>
                <Textarea
                  value={formData.company_boilerplate}
                  onChange={(e) => handleChange("company_boilerplate", e.target.value)}
                  placeholder="Enter your company boilerplate here"
                  className="mt-2 min-h-[100px]"
                  data-testid="boilerplate-textarea"
                />
              </div>

              {/* Video URL */}
              <div>
                <Label>Video URL</Label>
                <Input
                  value={formData.video_url}
                  onChange={(e) => handleChange("video_url", e.target.value)}
                  placeholder="Enter your promotional video URL here. (Example: https://youtu.be/rWh3CShk_doc)"
                  className="mt-2"
                  data-testid="video-url-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Public Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Public Contact Information</CardTitle>
              <p className="text-sm text-blue-600">
                This contact information will be displayed in your press release for media and public inquiries.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>
                    Organization <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.public_org}
                    onChange={(e) => handleChange("public_org", e.target.value)}
                    placeholder="Organization"
                    className="mt-2"
                    data-testid="public-org-input"
                  />
                </div>
                <div>
                  <Label>
                    Website URL <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.public_website}
                    onChange={(e) => handleChange("public_website", e.target.value)}
                    placeholder="Website URL"
                    className="mt-2"
                    data-testid="public-website-input"
                  />
                </div>
                <div>
                  <Label>
                    Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.public_name}
                    onChange={(e) => handleChange("public_name", e.target.value)}
                    placeholder="Name"
                    className="mt-2"
                    data-testid="public-name-input"
                  />
                </div>
                <div>
                  <Label>
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={formData.public_email}
                    onChange={(e) => handleChange("public_email", e.target.value)}
                    placeholder="Email"
                    className="mt-2"
                    data-testid="public-email-input"
                  />
                </div>
                <div>
                  <Label>Phone Number (Optional)</Label>
                  <Input
                    value={formData.public_phone}
                    onChange={(e) => handleChange("public_phone", e.target.value)}
                    placeholder="This number will be publicly displayed; skip if not preferred."
                    className="mt-2"
                    data-testid="public-phone-input"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.public_address}
                    onChange={(e) => handleChange("public_address", e.target.value)}
                    placeholder="Address"
                    className="mt-2"
                    data-testid="public-address-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Details */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                Additional Details
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  ⚠️
                </Badge>
              </CardTitle>
              <p className="text-sm text-amber-600">
                For PR Link Boost internal reference only. This information will NOT be published in the final press release.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Supporting Documents */}
              <div>
                <Label className="flex items-center gap-2">
                  Supporting Documents
                  <Info className="h-4 w-4 text-slate-400" />
                </Label>
                <div className="mt-2 flex items-center gap-4">
                  <span className="text-sm text-slate-600">Document 1</span>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                    <Button variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload File
                      </span>
                    </Button>
                  </label>
                  <span className="text-xs text-slate-500">
                    Up to 10MB per document, accepts .pdf, .jpg, .png, and .doc
                  </span>
                </div>
                {documents.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-2 bg-slate-100 rounded px-3 py-2"
                      >
                        <span className="text-sm text-slate-700 flex-1">{doc.name}</span>
                        <button
                          onClick={() => removeDocument(doc.id)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Internal Contact Information */}
              <div>
                <Label className="flex items-center gap-2 mb-4">
                  Internal Contact Information
                  <Info className="h-4 w-4 text-slate-400" />
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600">Organization</Label>
                    <Input
                      value={formData.internal_org}
                      onChange={(e) => handleChange("internal_org", e.target.value)}
                      placeholder="Organization"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Website URL</Label>
                    <Input
                      value={formData.internal_website}
                      onChange={(e) => handleChange("internal_website", e.target.value)}
                      placeholder="Website URL"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Name</Label>
                    <Input
                      value={formData.internal_name}
                      onChange={(e) => handleChange("internal_name", e.target.value)}
                      placeholder="Name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Email</Label>
                    <Input
                      value={formData.internal_email}
                      onChange={(e) => handleChange("internal_email", e.target.value)}
                      placeholder="Email"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Phone number</Label>
                    <Input
                      value={formData.internal_phone}
                      onChange={(e) => handleChange("internal_phone", e.target.value)}
                      placeholder="Phone number"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Address</Label>
                    <Input
                      value={formData.internal_address}
                      onChange={(e) => handleChange("internal_address", e.target.value)}
                      placeholder="Address"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 pb-8">
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={loading}
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
              data-testid="save-draft-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save As Draft
            </Button>
            <Button
              onClick={() => setStep(2)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-continue-btn"
            >
              Save & Continue
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Preview Your Press Release</CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{formData.title || "Untitled Press Release"}</h2>
              <p className="text-slate-600 italic mb-4">
                {formData.city}, {formData.country} — {formData.summary}
              </p>
              <div className="whitespace-pre-wrap text-slate-700" dangerouslySetInnerHTML={{ __html: formData.body || "No content" }} />
              {formData.company_boilerplate && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="text-lg font-semibold mb-2">About the Company</h3>
                  <p className="text-slate-600">{formData.company_boilerplate}</p>
                </div>
              )}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-semibold mb-2">Contact Information</h3>
                <p className="text-slate-600">
                  {formData.public_name} | {formData.public_org}<br />
                  {formData.public_email} | {formData.public_website}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Selected Channels */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Selected Distribution Channels</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {channels
                  .filter((c) => selectedChannels.includes(c.channel_id))
                  .map((channel) => (
                    <Badge key={channel.channel_id} variant="outline" className="px-3 py-1">
                      {channel.icon} {channel.name}
                    </Badge>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pb-8">
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              data-testid="back-btn"
            >
              Back to Edit
            </Button>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={loading}
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
              >
                <Save className="h-4 w-4 mr-2" />
                Save As Draft
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-pr-final-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {loading ? "Submitting..." : "Submit Press Release"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubmitPR;
